import { signal } from '@angular/core';
import { RestApi } from '@core/api/rest.api';
import { ApiResponse } from './apiresponse.model';

interface HasId {
  id?: number;

  [key: string]: any;
}

export abstract class ClassicDatasource<T extends HasId> extends RestApi<T> {
  emptyApiResponse = {
    success: true,
    totalRowCount: 0,
    data: [],
  };

  isLoaded = signal(false);
  error = signal<string | undefined>(undefined);
  data = signal<ApiResponse<T[]>>(this.emptyApiResponse);

  override async getList(params?: object, abortSignal?: AbortSignal) {
    this.isLoading.set(true);
    let response: ApiResponse<T[]> = this.emptyApiResponse;
    try {
      response = await super.getList(params, abortSignal);
      this.data.set(response);
    } catch (e) {
      console.error(e);
      response.success = false;
      if (e instanceof Error) {
        response.message = e.message;
        this.error.set(e.message);
      }
    } finally {
      this.isLoading.set(false);
      this.isLoaded.set(true);
    }
    return response;
  }

  override async create(value: T) {
    const newRecord = await super.create(value);
    this.insertLocalData(newRecord);
    return newRecord;
  }

  override async createMultipart(value: FormData) {
    const newRecord = await super.createMultipart(value);
    this.insertLocalData(newRecord);
    return newRecord;
  }

  insertLocalData(newRecord: T) {
    if (this.data) {
      this.data.update(old => {
        if (old) {
          const newData = [...old.data, newRecord];
          return { ...old, data: newData };
        }
        return old;
      });
    }
  }

  override async update(id: number, value: Partial<T>) {
    const updatedRecord = await super.update(id, value);
    this.updateLocalData(updatedRecord);
    return updatedRecord;
  }

  override async updateMultipart(id: number, value: FormData) {
    const updatedRecord = await super.updateMultipart(id, value);
    this.updateLocalData(updatedRecord);
    return updatedRecord;
  }

  updateLocalData(updatedRecord: T) {
    if (this.data) {
      this.data.update(old => {
        if (old) {
          const newData = old.data.map(r =>
            r.id == updatedRecord.id ? updatedRecord : r
          );
          return { ...old, data: newData };
        }
        return old;
      });
    }
  }

  override async remove(id: number) {
    const deletedRecord = await super.remove(id);
    this.removeLocalData(deletedRecord);
    return deletedRecord;
  }

  removeLocalData(deletedRecord: T) {
    if (this.data) {
      this.data.update(old => {
        if (old) {
          const newData = old.data.filter(r => r.id != deletedRecord.id);
          return { ...old, data: newData };
        }
        return old;
      });
    }
  }

  clear() {
    this.data.set(this.emptyApiResponse);
  }
}
